import {FontAwesomeIcon as Icon} from '@fortawesome/react-fontawesome'
import _ from 'lodash'
import React from "react"
import ClickOutside from "./ClickOutside"
import {translate} from '../filters'

type SelectInputProps = {
  value: string
  keyField: string
  labelField: string
  options: Array<any>
  setOption: (option: string | null) => void
  title?: string
  pin?: string
  required: boolean
}

type SelectInputState = {
  isOpen: boolean,
  highlightedIndex: number,
  options: Array<any>
  selected: string | null
  required: boolean
}

class SelectInput extends React.Component<SelectInputProps, SelectInputState> {
  private refNodes: Array<HTMLLIElement> = []

  constructor(props: SelectInputProps) {
    super(props)

    this.state = {
      isOpen: false,
      highlightedIndex: 0,
      options: this.props.options,
      selected: null,
      required: this.props.required
    }
  }

  componentDidMount = async () => {
    await this.select(this.props.value)
  }

  open = async () => {
    if (this.state.isOpen) {
      this.close()
      return
    }

    this.setState({
      isOpen: true
    })

    const index = _.findIndex(this.props.options, option => {
      return option[this.props.keyField] === this.props.value
    })

    await this.highlight(index >= 0 ? index : 0)

    this.scrollToHighlighted()
  }

  close = () => {
    if (!this.state.isOpen) {
      return
    }

    this.setState({
      isOpen: false
    })
  }

  select = async (option: string | null) => {
    this.props.setOption && await this.props.setOption(option)

    const selected = _.find(this.props.options, {[this.props.keyField]: this.props.value})

    this.setState({
      highlightedIndex: 0,
      selected: selected ? selected[this.props.labelField] : null
    })

    this.close()
  }

  selectHighlighted = async () => {
    const option = this.state.options[this.state.highlightedIndex]
    await this.select(option ? option[this.props.keyField] : option)
  }

  setRef = (node: HTMLLIElement) => {
    this.refNodes = [...this.refNodes, node]
  }

  scrollToHighlighted = () => {
    const element = _.get(this.refNodes, this.state.highlightedIndex)

    element && element.scrollIntoView({
      block: 'nearest',
    })
  }

  highlight = async (index: number) => {
    await this.setState({
      highlightedIndex: index
    })

    if (this.state.highlightedIndex < 0) {
      await this.setState({
        highlightedIndex: this.state.options.length - 1
      })
    }

    if (this.state.highlightedIndex > this.state.options.length - 1) {
      await this.setState({
        highlightedIndex: 0
      })
    }

    this.scrollToHighlighted()
  }

  clearSelected = async (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    this.select(null)
  }

  render() {
    const {isOpen, highlightedIndex, selected} = this.state
    const {title, keyField, labelField} = this.props
    return (
      <ClickOutside do={this.close}>
        <div className="relative">
          <button onClick={this.open} className="text-lg flex flex-row justify-between appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded px-4 py-2 leading-tight hover:bg-gray-200 focus:bg-gray-200 focus:border-gray-300 text-left focus:outline-none" ref="button" type="button">
            {
              selected ?
                <div className="font-semibold">
                  {translate(selected || '')}

                  {
                    this.props.required ? null :
                      <span onClick={this.clearSelected}>
                        <Icon icon="times" className="text-base text-gray-500"/>
                      </span>
                  }

                </div>
                :
                <span className="text-gray-500">{translate(title || '')}</span>
            }

            {
              isOpen ?
                <Icon icon="angle-up"/>
                :
                <Icon icon="angle-down"/>
            }
          </button>

          {
            isOpen ?
              <div className="w-full bg-gray-200 text-gray-700 absolute z-10 shadow-lg border-1 bottom-0">
                {
                  this.state.options.length > 0 ?
                    <ul className="w-full h-full max-h-50 overflow-y-scroll scrollbar">
                      {_.map(this.state.options, (option, i) => (
                        <li key={option[keyField]} onClick={() => this.select(option[keyField])} ref={this.setRef} className={"px-3 py-2 cursor-pointer hover:bg-gray-300" + (highlightedIndex === i ? ' bg-gray-300' : '')}>{translate(option[labelField])}</li>
                      ))}
                    </ul>
                    : null
                }
              </div>
              : null
          }

        </div>
      </ClickOutside>
    )
  }
}

export default SelectInput
